import { getSales } from 'api/order'
import { Button } from 'components/common/Button'
import { useAuth, useAuthModal, useDrawer } from 'contexts'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import { ReactNode, Suspense, lazy } from 'react'
import { useQuery } from 'react-query'
import { Edition } from 'utils/data'
const TradeTableHeading = dynamic(
  () =>
    import('components/common/TradeTableHeading').then(
      m => m.TradeTableHeading
    ),
  { ssr: false }
)
const BidsTable = lazy(() =>
  import('components/common/BidsTable').then(m => ({ default: m.BidsTable }))
)
const AsksTable = lazy(() =>
  import('components/common/AsksTable').then(m => ({ default: m.AsksTable }))
)
const SalesTable = lazy(() =>
  import('components/common/SalesTable').then(m => ({ default: m.SalesTable }))
)

type Props = {
  currentEdition?: Edition | null
  id: number
  hasSales: boolean
}

export const ViewPreviousTrades = ({ currentEdition, id, hasSales }: Props) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { setState: setAuthModalState, setIsOpen: setAuthModalIsOpen } =
    useAuthModal()
  const { setConfig: setDrawerConfig, setIsOpen: setDrawerIsOpen } = useDrawer()

  const { refetch: refetchSales } = useQuery(
    ['sales', id, currentEdition?.id],
    async () => {
      if (!id) return

      return await getSales({
        artworkId: id,
        editionName: currentEdition?.name || 'Main',
      })
    },
    {
      keepPreviousData: true,
      enabled: false,
    }
  )

  const onOpenDrawer = async (content: 'bids' | 'asks' | 'sales') => {
    if (!user) {
      setAuthModalState('register')
      setAuthModalIsOpen(true)
      return
    }

    let heading: ReactNode = null
    let body: ReactNode = null

    if (content === 'bids') {
      heading = <TradeTableHeading title='bids' />
      body = <BidsTable bids={currentEdition?.bids} />
    }

    if (content === 'asks') {
      heading = <TradeTableHeading title='listings' />
      body = <AsksTable asks={currentEdition?.asks} />
    }

    if (content === 'sales') {
      const { data } = await refetchSales()

      heading = <TradeTableHeading title='sales' />
      body = <SalesTable sales={data || []} />
    }

    if (heading && body) {
      setDrawerConfig({
        heading,
        body: (
          <Suspense fallback={<div className='h-full bg-cool-50' />}>
            {body}
          </Suspense>
        ),
        direction: 'right',
      })
      setDrawerIsOpen(true)
    }
  }

  return (
    <div className='my-6 flex space-x-4'>
      <Button
        onClick={() => onOpenDrawer('asks')}
        style='outline'
        className='w-full whitespace-nowrap'
        data-e2e='view-asks-button'
      >
        <span className='mr-1 [@media(max-width:419px)]:hidden'>
          {t('common:view')}
        </span>
        <span>{t('common:listings')}</span>
      </Button>
      <Button
        onClick={() => onOpenDrawer('bids')}
        style='outline'
        className='w-full whitespace-nowrap'
        data-e2e='view-bids-button'
      >
        <span className='mr-1 [@media(max-width:419px)]:hidden'>
          {t('common:view')}
        </span>
        <span>{t('common:offers')}</span>
      </Button>

      {hasSales && (
        <Button
          onClick={() => onOpenDrawer('sales')}
          style='outline'
          className='w-full whitespace-nowrap'
          data-e2e='view-sales-button'
        >
          <span className='mr-1 [@media(max-width:419px)]:hidden'>
            {t('common:view')}
          </span>
          <span>{t('common:sales')}</span>
        </Button>
      )}
    </div>
  )
}

ViewPreviousTrades.displayName = 'ViewPreviousTrades'
